<template>
  <el-form :model="form" ref="widgetSignupForm" id="widget-signup-form">
    <div class="fadeIn">
      <el-row v-if="socialAuth" type="flex">
        <el-col :span="24" class="social-auth">
          <p>
            Please provide additional information for
            <span>{{ userData.identifier }}</span>
          </p>
        </el-col>
      </el-row>
      <el-row
        v-for="(field, index) in socialAuth ? schema.slice(2) : schema"
        :key="index"
        type="flex"
      >
        <el-col :span="24">
          <component
            v-if="field.meta_data && field.meta_data.options"
            class="authx-input"
            :is="setComponent(field.field_type_id)"
            :label="field.label"
            :options="field.meta_data.options"
            :parentForm="form"
            :meta-data="field"
            :colors="colors"
            :form-index="index"
            :ref="`input-field-${index}`"
            :value.sync="form[field.name.toLowerCase()]"
            @isValid="isValid"
          />

          <component
            v-else
            class="authx-input"
            :field-types="fieldTypes"
            :is="setComponent(field.field_type_id)"
            :label="field.label"
            :parentForm="form"
            :meta-data="field"
            :colors="colors"
            :form-index="index"
            :ref="`input-field-${index}`"
            :value.sync="form[field.name.toLowerCase()]"
            @isValid="isValid"
          />
        </el-col>
      </el-row>
    </div>
    <el-row type="flex" class="my-1">
      <el-col :span="24" class="terms">
        <p>
          By clicking the submit button, you agree to the
          <a
            target="_blank"
            rel="noopener noreferrer"
            href="https://www.radius.africa/legal"
            >terms of service</a
          >
        </p>
      </el-col>
    </el-row>
    <el-row type="flex">
      <el-col :span="24">
        <el-button
          class="authx-button w-100"
          type="primary"
          :loading="loading"
          :disabled="preview"
          @click="submit"
          >{{ socialAuth ? "Complete" : "Signup" }}</el-button
        >
      </el-col>
    </el-row>
    <template v-if="!socialAuth">
      <el-row>
        <el-col :span="24">
          <p class="form-footer">
            Already have an account?
            <span @click="$emit('navigate', 'login')">Login</span>
          </p>
        </el-col>
      </el-row>
      <form-social
        :label="'Sign up with'"
        :google-auth="googleAuth"
        :facebook-auth="facebookAuth"
        :session-token="domainData.sessionToken"
        :colors="colors"
      />
    </template>
  </el-form>
</template>

<script>
import TextInput from "@/components/Products/AuthX/Widget/FormInputs/TextInput";
import TextArea from "@/components/Products/AuthX/Widget/FormInputs/TextArea";
import Checkbox from "@/components/Products/AuthX/Widget/FormInputs/Checkbox";
import DatePicker from "@/components/Products/AuthX/Widget/FormInputs/DatePicker";
import Dropdown from "@/components/Products/AuthX/Widget/FormInputs/Dropdown";
import File from "@/components/Products/AuthX/Widget/FormInputs/File";
import Radio from "@/components/Products/AuthX/Widget/FormInputs/Radio";
import FormSuccess from "@/components/Products/AuthX/Widget/FormSuccess";
import FormLivenessCheck from "@/components/Products/AuthX/Widget/FormLivenessCheck";
import FormLocationCheck from "@/components/Products/AuthX/Widget/FormLocationCheck";
import FormDocumentUpload from "@/components/Products/AuthX/Widget/FormDocumentUpload";
import apiConstants from "@/apiConstants";
import { currentUser } from "@/lib/utils";
import FormSocial from "@/components/Products/AuthX/Widget/Forms/FormSocial.vue";

export default {
  name: "FormSignup",
  props: {
    schema: {
      type: Array,
      required: true
    },
    mfa: {
      type: Boolean,
      required: true
    },
    fieldTypes: {
      type: Array,
      default() {
        return [];
      }
    },
    domainData: {
      type: Object,
      default() {
        return {};
      }
    },
    colors: {
      type: Object,
      default() {
        return {};
      }
    },
    googleAuth: {
      type: [Number, Boolean],
      default: false
    },
    facebookAuth: {
      type: [Number, Boolean],
      default: false
    },
    socialAuth: {
      type: Boolean,
      default: false
    },
    userData: {
      type: Object,
      default() {
        return {};
      }
    }
  },
  components: {
    FormSocial,
    FormDocumentUpload,
    FormLocationCheck,
    FormLivenessCheck,
    FormSuccess,
    TextInput,
    TextArea,
    Checkbox,
    DatePicker,
    Dropdown,
    File,
    Radio
  },
  data() {
    return {
      radiusUser: currentUser(),
      form: {},
      isValidFrom: true,
      loading: false,
      socialType: ""
    };
  },

  inject: ["preview"],

  computed: {
    preview() {
      return this.preview;
    }
  },

  created() {
    this.schema.forEach(field => {
      this.form[field.name.toLowerCase()] = "";
    });
  },

  methods: {
    updateSocialAuthUser() {
      const apiUrl = `apps/users/data`;

      this.$http
        .post(
          apiUrl,
          {
            body: this.form,
            method: "PUT"
          },
          {
            headers: {
              "Session-Token": localStorage.getItem("vendor-session-token"),
              "data-baseurl-namespace": apiConstants.URLS.AUTHX
            }
          }
        )
        .then(response => {
          this.loading = false;
          const resData = response.data;
          if (resData.status === "error") {
            this.handleErrors(resData);
          } else {
            if (resData.action === "oauth_redirect") {
              localStorage.removeItem("vendor-session-token");
              this.notification(
                "Completed",
                "User registration completed successfully",
                "success"
              );
              let redirectUrl = resData.data.redirect_uri[0];
              if (this.domainData.authxState) {
                redirectUrl += `&__authx_state=${this.domainData.authxState}`;
              }
              window.location = redirectUrl;
            } else {
              this.notification("Error", "Something went wrong", "danger");
            }
          }
        })
        .catch(() => {
          this.loading = false;
          this.notification("Error", "An unknown error occurred", "danger");
        });
    },

    isValid(val) {
      this.isValidFrom = val;
    },

    setComponent(typeId) {
      const fieldType = this.fieldTypes.find(e => e.id === typeId);
      switch (fieldType.label) {
        case "Text":
          return "text-input";
        case "Number":
          return "text-input";
        case "Email":
          return "text-input";
        case "Password":
          return "text-input";
        case "Text Area":
          return "text-area";
        case "Checkbox":
          return "checkbox";
        case "Dropdown":
          return "dropdown";
        case "Radio":
          return "radio";
        case "Datepicker":
          return "date-picker";
        default:
          break;
      }
    },

    submit() {
      this.loading = true;
      this.isValidFrom = true;

      if (this.socialAuth) {
        this.schema.slice(2).forEach((item, i) => {
          this.$refs[`input-field-${i}`][0].validateInput();
        });
      } else {
        this.schema.forEach((item, i) => {
          this.$refs[`input-field-${i}`][0].validateInput();
        });
      }

      if (!this.isValidFrom) {
        this.$message.error("Some required fields are still missing.");
        this.loading = false;
        return false;
      }

      if (this.radiusUser) {
        this.$message.error("This is only for preview!");
        this.loading = false;
        return false;
      }

      if (this.socialAuth) {
        this.updateSocialAuthUser();
        return;
      }

      const formKeys = Object.keys(this.form);
      const identifierKey = formKeys.find(
        fk => fk.startsWith("user_auth_identifier_") > 0
      );
      const passwordKey = formKeys.find(
        fk => fk.startsWith("user_auth_password_") > 0
      );

      const identifier = this.form[identifierKey];
      const password = this.form[passwordKey];

      delete this.form[identifierKey];
      delete this.form[passwordKey];

      const formBody = {
        identifier: identifier,
        password: password,
        data: this.form
      };

      this.$http
        .post(
          "apps/users",
          {
            body: formBody,
            method: "POST"
          },
          {
            headers: {
              "Session-Token": this.domainData.sessionToken
            }
          }
        )
        .then(response => {
          this.loading = false;
          const resData = response.data;
          if (resData.status === "error") {
            this.handleErrors(resData);
            this.gaTrack("Failed");
          } else {
            this.notification(
              "Completed",
              "User registered successfully",
              "success"
            );
            this.$emit("next", {
              action: resData.data.action,
              identifier: formBody.identifier,
              token: resData.data.data.token,
              redirect_uri: resData.redirect_uri ? resData.redirect_uri : null
            });
            this.gaTrack("Successful", resData.data.action);
          }
        })
        .catch(() => {
          this.loading = false;
          this.notification("Error", "An unknown error occurred", "danger");
        });
    },

    gaTrack(type, action) {
      this.$ga.event({
        eventCategory: "AuthX Widget",
        eventAction: `Signup`,
        eventLabel: type,
        eventValue: action ? action : "-"
      });
    }
  }
};
</script>

<style lang="scss" scoped>
.social-auth {
  margin-bottom: 30px;
  text-align: center;

  p {
    color: var(--ra-grey-tertiary);
  }

  span {
    font-weight: 600;
    color: var(--ra-grey-primary);
  }
}
.terms {
  text-align: center;
  p {
    color: #777;
    font-size: 14px !important;
  }

  a {
    font-size: 14px !important;
    font-weight: normal;
  }
}
</style>
